import {
  BookOpen,
  Building2,
  Calculator,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  Gavel,
  Inbox,
  Receipt,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  AVISO_DE_EXPORTACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { Grupo, QuadroDeEstado, Secao } from './componentes';
import { GraficoComparativoPorDre, GraficoDeTendencia } from './graficos';
import type {
  GrupoDeRelatorios,
  ItemDeBacklog,
  LinhaPorCredenciado,
  LinhaPorDre,
  LinhaPorPeriodo,
  RelatorioOficial,
  RelatoriosAnaliticos,
} from './contrato';
import { carregarRelatorios } from './dados';
import { useColecao } from './useColecao';

/**
 * ONDA-CL2 — relatórios, cinco abas.
 *
 * Não confundir com `/admin/relatorios`, que é a montagem de rastreabilidade e
 * exportação de documento — outra tela, outro fluxo. A separação está registrada
 * em `docs/rastreabilidade/matriz-paridade-analises.md`.
 *
 * **O catálogo oficial tem seis grupos, não quatro cartões.** A primeira versão
 * desta tela leu os títulos de grupo do protótipo ("Termos de Adesão", "Ordens
 * de Serviço") como se fossem relatórios, e por isso mostrava quatro cartões
 * planos onde há seis grupos com oito relatórios.
 *
 * **Os três botões de exportação de cada relatório vêm, e não exportam.** Decisão
 * do dono em 2026-09-08: o cartão do catálogo oficial fica igual ao do protótipo
 * — ícone do grupo à esquerda, `PDF oficial`, `XLSX` e `CSV` embaixo. Nenhum
 * endpoint gera esses arquivos (`apps/api/src/modulos/` só tem `eu`, `govbr` e
 * `senha`), então cada um mostra o aviso de indisponibilidade em vez de abrir um
 * download que nunca vem — mesmo padrão de `SlaAprovacaoSmePage`. É a exceção
 * consciente à regra 7 de interface, não esquecimento dela.
 *
 * O `Exportar CSV` das abas de tabela continua fora: lá o botão não pertence a um
 * relatório do catálogo, e o protótipo não o desenha dentro de cartão nenhum.
 *
 * O intervalo do período fica na URL junto com a aba: um relatório sem o recorte
 * que o gerou não é conferível por quem recebe o link.
 */

const ABAS = ['oficiais', 'dre', 'credenciado', 'periodo', 'backlog'] as const;
type Aba = (typeof ABAS)[number];

function abaValida(valor: string | null): Aba {
  return ABAS.includes(valor as Aba) ? (valor as Aba) : 'oficiais';
}

function relatoriosVazios(dado: RelatoriosAnaliticos): boolean {
  return (
    dado.oficiais.length === 0 &&
    dado.porDre.length === 0 &&
    dado.porCredenciado.length === 0 &&
    dado.porPeriodo.length === 0 &&
    dado.backlogDtp.length === 0
  );
}

const COLUNAS_DRE: DataTableColumn<LinhaPorDre>[] = [
  { header: 'DRE', accessorKey: 'dre', sortKey: 'dre' },
  { header: 'Código', accessorKey: 'codigo' },
  { header: 'Credenciados', accessorKey: 'credenciados', sortKey: 'credenciados' },
  { header: 'OS', accessorKey: 'ordensServico', sortKey: 'ordensServico' },
  { header: 'Medições', accessorKey: 'medicoes', sortKey: 'medicoes' },
  { header: 'Valor Total', cell: (l) => <MoneyDisplay value={l.valorTotal} /> },
];

const COLUNAS_CREDENCIADO: DataTableColumn<LinhaPorCredenciado>[] = [
  { header: 'Nome', accessorKey: 'nome', sortKey: 'nome' },
  { header: 'CPF/CNPJ', accessorKey: 'documento' },
  { header: 'Termos', accessorKey: 'termos', sortKey: 'termos' },
  { header: 'OS', accessorKey: 'ordensServico', sortKey: 'ordensServico' },
  { header: 'Medições', accessorKey: 'medicoes', sortKey: 'medicoes' },
  { header: 'Valor Total', cell: (l) => <MoneyDisplay value={l.valorTotal} /> },
  { header: 'Multas', accessorKey: 'multas', sortKey: 'multas' },
  {
    header: 'Status',
    cell: (l) => <Badge variant={l.status === 'ativo' ? 'default' : 'outline'}>{l.status}</Badge>,
  },
];

const COLUNAS_PERIODO: DataTableColumn<LinhaPorPeriodo>[] = [
  { header: 'Mês', accessorKey: 'mes', sortKey: 'mes' },
  { header: 'OS Emitidas', accessorKey: 'osEmitidas', sortKey: 'osEmitidas' },
  { header: 'OS Concluídas', accessorKey: 'osConcluidas', sortKey: 'osConcluidas' },
  { header: 'Medições Aprovadas', accessorKey: 'medicoesAprovadas', sortKey: 'medicoesAprovadas' },
  { header: 'Valor Medições', cell: (l) => <MoneyDisplay value={l.valorMedicoes} /> },
  { header: 'Multas', accessorKey: 'multas', sortKey: 'multas' },
];

function ListaDeBacklog({ itens }: { itens: readonly ItemDeBacklog[] }) {
  if (itens.length === 0) {
    return (
      <EmptyState
        title="Backlog vazio"
        description="Nenhum relatório pendente de elaboração."
        variant="search"
      />
    );
  }

  return (
    <ul className="space-y-2">
      {itens.map((item) => (
        <li key={item.id} className="rounded-lg border border-border p-4">
          <h3 className="text-sm font-semibold">{item.relatorio}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{item.descricao}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Ícone de cada grupo do catálogo oficial.
 *
 * É um por **grupo**, não um por relatório: no protótipo os dois cartões de
 * "Termos de Adesão" trazem a mesma prancheta. Chaveado por `grupo.chave`, que
 * é estável, e não pelo título, que é texto de tela.
 */
const ICONE_DO_GRUPO: Readonly<Record<string, LucideIcon>> = {
  termos: ClipboardList,
  'ordens-servico': ClipboardCheck,
  credenciados: Users,
  multas: Gavel,
  damsp: Receipt,
  apuracao: Calculator,
};

/** Os três formatos que o cartão do protótipo oferece, na ordem em que aparecem. */
const FORMATOS = [
  { rotulo: 'PDF oficial', icone: FileText },
  { rotulo: 'XLSX', icone: FileSpreadsheet },
  { rotulo: 'CSV', icone: Download },
] as const;

/** Um cartão do catálogo: ícone do grupo, título, descrição e os três formatos. */
function CartaoDeRelatorio({
  relatorio,
  icone: Icone,
}: {
  relatorio: RelatorioOficial;
  icone: LucideIcon;
}) {
  return (
    <li className="rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <Icone className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="text-sm font-semibold">{relatorio.titulo}</h3>
            {relatorio.pendenteDtp && <Badge variant="outline">Pendente DTP</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{relatorio.descricao}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FORMATOS.map(({ rotulo, icone: IconeDoFormato }) => (
          <Button
            key={rotulo}
            type="button"
            variant="outline"
            size="sm"
            /* O rótulo visível repete em oito cartões; o acessível diz de qual relatório é. */
            aria-label={`${rotulo} — ${relatorio.titulo}`}
            onClick={() => mostrarAviso(AVISO_DE_EXPORTACAO_INDISPONIVEL)}
          >
            <IconeDoFormato className="h-4 w-4" aria-hidden="true" />
            {rotulo}
          </Button>
        ))}
      </div>
    </li>
  );
}

/** Um grupo do catálogo oficial, com os relatórios que ele reúne. */
function GrupoOficial({ grupo }: { grupo: GrupoDeRelatorios }) {
  const icone = ICONE_DO_GRUPO[grupo.chave] ?? BookOpen;

  return (
    <Grupo titulo={grupo.titulo} caixaAlta>
      <ul className="grid gap-3 md:grid-cols-2">
        {grupo.relatorios.map((relatorio) => (
          <CartaoDeRelatorio key={relatorio.chave} relatorio={relatorio} icone={icone} />
        ))}
      </ul>
    </Grupo>
  );
}

export function RelatoriosAnaliticosPage() {
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaValida(parametros.get('aba'));
  const inicio = parametros.get('inicio') ?? '';
  const fim = parametros.get('fim') ?? '';
  const { estado, recarregar } = useColecao(carregarRelatorios, relatoriosVazios);

  function definir(chave: string, valor: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      if (valor) {
        proximo.set(chave, valor);
      } else {
        proximo.delete(chave);
      }
      return proximo;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Análises e notificações"
        title="Relatórios"
        description="Relatórios consolidados e oficiais do sistema TEG"
      />

      <QuadroDeEstado
        estado={estado}
        recarregar={recarregar}
        tituloVazio="Sem relatórios no recorte"
        descricaoVazio="Nenhuma consolidação disponível. Ajuste o período ou aguarde o fechamento da competência."
      >
        {(dado) => (
          <Tabs value={aba} onValueChange={(nova) => definir('aba', nova)}>
            <TabsList>
              <TabsTrigger value="oficiais">
                <BookOpen className="h-4 w-4" aria-hidden="true" />
                Oficiais
              </TabsTrigger>
              <TabsTrigger value="dre">
                <Building2 className="h-4 w-4" aria-hidden="true" />
                Por DRE
              </TabsTrigger>
              <TabsTrigger value="credenciado">
                <Users className="h-4 w-4" aria-hidden="true" />
                Por Credenciado
              </TabsTrigger>
              <TabsTrigger value="periodo">
                <CalendarRange className="h-4 w-4" aria-hidden="true" />
                Por Período
              </TabsTrigger>
              <TabsTrigger value="backlog">
                <Inbox className="h-4 w-4" aria-hidden="true" />
                Backlog DTP
              </TabsTrigger>
            </TabsList>

            <TabsContent value="oficiais" className="space-y-4">
              <Secao
                titulo="Filtros do período"
                descricao="Aplica-se aos relatórios que utilizam datas (multas, DAMSP, apuração, rescisões, substituições). Catálogo formal pendente de validação com a DTP."
              >
                <div className="grid gap-4 sm:grid-cols-2 lg:max-w-md">
                  <div className="space-y-1.5">
                    <Label htmlFor="periodo-inicio">Início</Label>
                    <Input
                      id="periodo-inicio"
                      type="date"
                      value={inicio}
                      onChange={(evento) => definir('inicio', evento.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="periodo-fim">Fim</Label>
                    <Input
                      id="periodo-fim"
                      type="date"
                      value={fim}
                      onChange={(evento) => definir('fim', evento.target.value)}
                    />
                  </div>
                </div>
              </Secao>

              {dado.oficiais.map((grupo) => (
                <GrupoOficial key={grupo.chave} grupo={grupo} />
              ))}
            </TabsContent>

            <TabsContent value="dre" className="space-y-4">
              <Secao titulo="Comparativo por DRE">
                <GraficoComparativoPorDre dados={dado.porDre} />
              </Secao>

              <DataTable
                columns={COLUNAS_DRE}
                data={[...dado.porDre]}
                emptyTitle="Sem diretoria no recorte"
                emptyDescription="Nenhuma DRE com movimento no período escolhido."
                ariaLabel="Comparativo por DRE"
              />
            </TabsContent>

            <TabsContent value="credenciado">
              <DataTable
                columns={COLUNAS_CREDENCIADO}
                data={[...dado.porCredenciado]}
                emptyTitle="Sem credenciado no recorte"
                emptyDescription="Nenhum credenciado com movimento no período escolhido."
                ariaLabel="Consolidado por credenciado"
              />
            </TabsContent>

            <TabsContent value="periodo" className="space-y-4">
              <Secao titulo="Evolução Mensal">
                <GraficoDeTendencia dados={dado.porPeriodo} />
              </Secao>

              <DataTable
                columns={COLUNAS_PERIODO}
                data={[...dado.porPeriodo]}
                emptyTitle="Sem competência fechada"
                emptyDescription="Nenhuma competência consolidada até agora."
                ariaLabel="Evolução mensal"
              />
            </TabsContent>

            <TabsContent value="backlog">
              <Secao
                titulo="Backlog de relatórios solicitados — DTP"
                descricao="Lista provisória de relatórios extras pedidos pela DTP. Cada item será validado e priorizado na próxima reunião antes de virar entrega definitiva no catálogo oficial."
              >
                <ListaDeBacklog itens={dado.backlogDtp} />
              </Secao>
            </TabsContent>
          </Tabs>
        )}
      </QuadroDeEstado>
    </div>
  );
}

export default RelatoriosAnaliticosPage;
